<template>
  <div
    class="consultant-header-global-search-section consultant-global-search"
    :class="{ 'consultant-global-search--open': isOpen && query.trim().length >= minChars }"
    ref="root"
  >
    <label v-if="!hideLabel" :for="inputId" class="consultant-global-search-field-label">{{ fieldLabel }}</label>
    <div class="consultant-global-search-control">
      <div class="consultant-global-search-wrap" :class="{ 'consultant-global-search-wrap--open': isOpen && query.trim().length >= minChars }" ref="wrap">
      <i class="bi bi-search consultant-global-search-icon" aria-hidden="true"></i>
      <input
        :id="inputId"
        ref="input"
        type="text"
        class="consultant-global-search-input"
        :placeholder="selectedLabel ? '' : effectivePlaceholder"
        :value="inputDisplay"
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-controls="listboxId"
        @input="onInput"
        @focus="onFocus"
        @keydown="onKeydown"
      />
      <button
        v-if="selectedLabel || query"
        type="button"
        class="consultant-global-search-clear"
        aria-label="Clear search"
        title="Clear"
        @mousedown.prevent
        @click="clear"
      >
        <i class="bi bi-x-lg" aria-hidden="true"></i>
      </button>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="consultant-global-search-panel">
        <div
          v-if="isOpen && query.trim().length >= minChars"
          :id="listboxId"
          ref="panel"
          class="consultant-global-search-panel consultant-global-search-panel--teleported"
          :style="panelStyle"
          role="listbox"
        >
        <template v-if="hasResults">
          <div class="consultant-global-search-panel-summary">
            {{ flatResults.length }} result{{ flatResults.length === 1 ? '' : 's' }}
          </div>
          <div ref="panelBody" class="consultant-global-search-panel-body">
            <ul class="consultant-global-search-group-list consultant-global-search-group-list--ranked">
              <li
                v-for="(item, index) in flatResults"
                :key="item.type + '-' + item.id"
                role="option"
                class="consultant-global-search-option"
                :class="{
                  'consultant-global-search-option--active': flatIndex === index,
                  'consultant-global-search-option--has-sub': !!item.subtitle
                }"
                @mousedown.prevent="selectItem(item)"
              >
                <span
                  class="consultant-global-search-option-icon"
                  :class="'consultant-global-search-option-icon--' + item.type"
                >
                  <i :class="iconClassForType(item.type)" aria-hidden="true"></i>
                </span>
                <span class="consultant-global-search-option-body">
                  <span class="consultant-global-search-option-name">
                    <template v-for="(part, pi) in getHighlightParts(item.name)" :key="'nm-' + pi">
                      <mark v-if="part.match" class="consultant-global-search-highlight">{{ part.text }}</mark>
                      <template v-else>{{ part.text }}</template>
                    </template>
                  </span>
                  <span v-if="item.subtitle" class="consultant-global-search-option-sub">{{ item.subtitle }}</span>
                </span>
                <span class="consultant-global-search-option-kind">{{ typeLabel(item.type) }}</span>
              </li>
            </ul>
          </div>
          <footer class="consultant-global-search-panel-footer">
            <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
            <span><kbd>Enter</kbd> select</span>
            <span><kbd>Esc</kbd> close</span>
          </footer>
        </template>
        <div v-else class="consultant-global-search-panel-empty">
          <i class="bi bi-search" aria-hidden="true"></i>
          <p>No matches for &ldquo;{{ query.trim() }}&rdquo;</p>
          <span>Try another name or check spelling</span>
        </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
let inputIdCounter = 0;

export default {
  name: 'CenterGlobalSearch',
  props: {
    consultants: {
      type: Array,
      default: () => []
    },
    customers: {
      type: Array,
      default: () => []
    },
    vendors: {
      type: Array,
      default: () => []
    },
    invoices: {
      type: Array,
      default: () => []
    },
    receivePayments: {
      type: Array,
      default: () => []
    },
    /** Field label above the input (default: Global Search). */
    label: {
      type: String,
      default: 'Global Search'
    },
    /** When true, omit the built-in label (parent supplies its own). */
    hideLabel: {
      type: Boolean,
      default: false
    },
    selectedLabel: {
      type: String,
      default: ''
    },
    /** Override placeholder; otherwise derived from which entity lists are provided. */
    placeholder: {
      type: String,
      default: ''
    },
    /** Sync typed query from parent (e.g. side menu list filter). */
    externalQuery: {
      type: String,
      default: ''
    }
  },
  emits: ['select', 'clear', 'query-change'],
  data() {
    inputIdCounter += 1;
    return {
      inputId: `consultantGlobalSearch-${inputIdCounter}`,
      query: '',
      isOpen: false,
      flatIndex: -1,
      minChars: 2,
      maxResults: 12,
      panelStyle: {},
    };
  },
  computed: {
    listboxId() {
      return `${this.inputId}-listbox`;
    },
    fieldLabel() {
      const t = String(this.label || '').trim();
      return t || 'Global Search';
    },
    effectivePlaceholder() {
      if (this.placeholder && String(this.placeholder).trim()) {
        return this.placeholder;
      }
      const hasConsultants = Array.isArray(this.consultants) && this.consultants.length > 0;
      const hasCustomers = Array.isArray(this.customers) && this.customers.length > 0;
      const hasVendors = Array.isArray(this.vendors) && this.vendors.length > 0;
      const hasInvoices = Array.isArray(this.invoices) && this.invoices.length > 0;
      const hasReceivePayments =
        Array.isArray(this.receivePayments) && this.receivePayments.length > 0;
      if (hasInvoices && hasReceivePayments && !hasConsultants && !hasVendors && !hasCustomers) {
        return 'Search invoice, receive payment...';
      }
      if (hasInvoices && hasCustomers && hasConsultants && !hasVendors) {
        return 'Search consultant, customer, invoice, payment...';
      }
      if (hasCustomers && !hasConsultants && !hasVendors) {
        return 'Search customers...';
      }
      if (hasConsultants && !hasCustomers && !hasVendors) {
        return 'Search consultants...';
      }
      if (hasVendors && !hasConsultants && !hasCustomers) {
        return 'Search vendors...';
      }
      if (hasConsultants && hasCustomers && !hasVendors) {
        return 'Search consultant, customer...';
      }
      if (hasConsultants && hasVendors && !hasCustomers) {
        return 'Search consultant, vendor...';
      }
      if (hasCustomers && hasVendors && !hasConsultants) {
        return 'Search customer, vendor...';
      }
      return 'Search consultant, customer, vendor...';
    },
    inputDisplay() {
      if (this.selectedLabel && !this.isOpen) {
        return this.selectedLabel;
      }
      return this.query;
    },
    normalizedQuery() {
      return (this.query || '').trim().toLowerCase();
    },
    flatResults() {
      const q = this.normalizedQuery;
      if (q.length < this.minChars) return [];

      const ranked = [];

      for (const c of this.consultants) {
        const score = this.scoreConsultantMatch(c, q);
        if (score > 0) {
          ranked.push({
            type: 'consultant',
            id: c.id,
            name: c.consultantName || c.name || 'Consultant',
            subtitle: this.formatConsultantSubtitle(c),
            score
          });
        }
      }

      for (const c of this.customers) {
        const score = this.scoreTextMatch(c.customerName, q, {
          exact: 1200,
          prefix: 700,
          contains: 400
        });
        if (score > 0) {
          ranked.push({
            type: 'customer',
            id: c.id,
            name: c.customerName || 'Customer',
            subtitle: '',
            score
          });
        }
      }

      for (const v of this.vendors) {
        const nameScore = this.scoreTextMatch(v.vendorName || v.name, q, {
          exact: 1200,
          prefix: 700,
          contains: 400
        });
        const codeScore = this.scoreTextMatch(v.vendorId, q, {
          exact: 800,
          prefix: 400,
          contains: 150
        });
        const score = Math.max(nameScore, codeScore);
        if (score > 0) {
          ranked.push({
            type: 'vendor',
            id: v.id ?? v.vendorId,
            name: v.vendorName || v.name || 'Vendor',
            subtitle: v.vendorId ? `ID: ${v.vendorId}` : '',
            score
          });
        }
      }

      for (const inv of this.invoices) {
        const score = this.scoreInvoiceMatch(inv, q);
        if (score > 0) {
          ranked.push({
            type: 'invoice',
            id: inv.id,
            name: inv.invoiceNo || inv.name || 'Invoice',
            subtitle: this.formatInvoiceSubtitle(inv),
            score
          });
        }
      }

      for (const rp of this.receivePayments) {
        const score = this.scoreReceivePaymentMatch(rp, q);
        if (score > 0) {
          ranked.push({
            type: 'receivePayment',
            id: rp.id,
            name: rp.reference || rp.paymentNo || rp.name || 'Receive payment',
            subtitle: this.formatReceivePaymentSubtitle(rp),
            score
          });
        }
      }

      return ranked
        .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
        .slice(0, this.maxResults)
        .map(({ type, id, name, subtitle }) => ({ type, id, name, subtitle }));
    },
    hasResults() {
      return this.flatResults.length > 0;
    }
  },
  watch: {
    selectedLabel(val) {
      if (val) {
        this.query = '';
        this.isOpen = false;
        this.flatIndex = -1;
      }
    },
    externalQuery(val) {
      if (!this.selectedLabel) {
        this.query = String(val || '');
      }
    },
    isOpen(open) {
      if (open && this.query.trim().length >= this.minChars) {
        this.$nextTick(() => {
          this.updatePanelPosition();
          this.attachPositionListeners();
        });
      } else {
        this.detachPositionListeners();
        this.panelStyle = {};
      }
    },
    query() {
      if (this.isOpen && this.query.trim().length >= this.minChars) {
        this.$nextTick(() => {
          this.updatePanelPosition();
          this.$nextTick(() => this.updatePanelPosition());
        });
      }
    },
    flatResults() {
      if (this.isOpen && this.query.trim().length >= this.minChars) {
        this.$nextTick(() => this.updatePanelPosition());
      }
    },
  },
  methods: {
    scoreTextMatch(text, query, weights) {
      const t = String(text || '').trim().toLowerCase();
      const q = String(query || '').trim().toLowerCase();
      if (!q || !t) return 0;
      if (t === q) return weights.exact;
      if (t.startsWith(q)) return weights.prefix;
      if (t.includes(q)) return weights.contains;
      return 0;
    },
    scoreConsultantMatch(consultant, query) {
      const name = consultant.consultantName || consultant.name || '';
      const code = consultant.consultantId || '';
      const nameScore = this.scoreTextMatch(name, query, {
        exact: 1000,
        prefix: 600,
        contains: 300
      });
      const codeScore = this.scoreTextMatch(code, query, {
        exact: 800,
        prefix: 400,
        contains: 150
      });
      let linkedCustomerScore = 0;
      const names = consultant.customerNames;
      if (Array.isArray(names)) {
        for (const customerName of names) {
          linkedCustomerScore = Math.max(
            linkedCustomerScore,
            this.scoreTextMatch(customerName, query, {
              exact: 80,
              prefix: 50,
              contains: 30
            })
          );
        }
      }
      return Math.max(nameScore, codeScore, linkedCustomerScore);
    },
    scoreInvoiceMatch(invoice, query) {
      const no = invoice.invoiceNo || invoice.name || '';
      const noScore = this.scoreTextMatch(no, query, {
        exact: 1100,
        prefix: 650,
        contains: 380
      });
      const dateScore = this.scoreTextMatch(
        invoice.invoiceDateLabel || invoice.invoiceDate,
        query,
        { exact: 500, prefix: 280, contains: 120 }
      );
      const customerScore = this.scoreTextMatch(invoice.customerName, query, {
        exact: 400,
        prefix: 220,
        contains: 100
      });
      return Math.max(noScore, dateScore, customerScore);
    },
    scoreReceivePaymentMatch(payment, query) {
      const ref = payment.reference || payment.paymentNo || payment.name || '';
      const refScore = this.scoreTextMatch(ref, query, {
        exact: 1100,
        prefix: 650,
        contains: 380
      });
      const dateScore = this.scoreTextMatch(
        payment.paymentDateLabel || payment.paymentDate,
        query,
        { exact: 500, prefix: 280, contains: 120 }
      );
      const customerScore = this.scoreTextMatch(payment.customerName, query, {
        exact: 400,
        prefix: 220,
        contains: 100
      });
      const idScore = this.scoreTextMatch(payment.id, query, {
        exact: 700,
        prefix: 350,
        contains: 120
      });
      return Math.max(refScore, dateScore, customerScore, idScore);
    },
    formatInvoiceSubtitle(invoice) {
      const parts = [];
      if (invoice.invoiceDateLabel) {
        parts.push(invoice.invoiceDateLabel);
      } else if (invoice.invoiceDate) {
        parts.push(String(invoice.invoiceDate));
      }
      if (invoice.customerName) {
        parts.push(invoice.customerName);
      }
      return parts.join(' - ');
    },
    formatReceivePaymentSubtitle(payment) {
      const parts = [];
      if (payment.paymentDateLabel) {
        parts.push(payment.paymentDateLabel);
      } else if (payment.paymentDate) {
        parts.push(String(payment.paymentDate));
      }
      if (payment.customerName) {
        parts.push(payment.customerName);
      }
      return parts.join(' - ');
    },
    iconClassForType(type) {
      if (type === 'customer') return 'bi bi-briefcase';
      if (type === 'vendor') return 'bi bi-truck';
      if (type === 'invoice') return 'bi bi-file-earmark-text';
      if (type === 'receivePayment') return 'bi bi-cash-stack';
      return 'bi bi-person';
    },
    typeLabel(type) {
      if (type === 'customer') return 'Customer';
      if (type === 'vendor') return 'Vendor';
      if (type === 'invoice') return 'Invoice';
      if (type === 'receivePayment') return 'Receive payment';
      return 'Consultant';
    },
    formatConsultantSubtitle(consultant) {
      const parts = [];
      if (consultant.consultantType) {
        parts.push(consultant.consultantType);
      }
      const names = consultant.customerNames;
      if (Array.isArray(names) && names.length > 0) {
        parts.push(
          names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2}` : '')
        );
      } else if (consultant.consultantId) {
        parts.push(consultant.consultantId);
      }
      return parts.join(' · ');
    },
    scrollActiveOptionIntoView() {
      this.$nextTick(() => {
        const body = this.$refs.panelBody;
        if (!body || this.flatIndex < 0) return;
        const active = body.querySelector('.consultant-global-search-option--active');
        if (active) {
          active.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      });
    },
    getHighlightParts(text) {
      const raw = String(text || '');
      const q = this.normalizedQuery;
      if (!q || !raw) return [{ text: raw, match: false }];
      const lower = raw.toLowerCase();
      const idx = lower.indexOf(q);
      if (idx < 0) return [{ text: raw, match: false }];
      const parts = [];
      if (idx > 0) parts.push({ text: raw.slice(0, idx), match: false });
      parts.push({ text: raw.slice(idx, idx + q.length), match: true });
      if (idx + q.length < raw.length) {
        parts.push({ text: raw.slice(idx + q.length), match: false });
      }
      return parts;
    },
    onDocumentMouseDown(e) {
      const root = this.$refs.root;
      const panel = this.$refs.panel;
      const target = e.target;
      if (
        (root && root.contains(target)) ||
        (panel && panel.contains(target))
      ) {
        return;
      }
      this.isOpen = false;
      this.flatIndex = -1;
      if (this.selectedLabel) {
        this.query = '';
      }
    },
    updatePanelPosition() {
      const wrap = this.$refs.wrap;
      if (!wrap || !this.isOpen) return;

      const rect = wrap.getBoundingClientRect();
      const gap = 6;
      const viewportPad = 12;
      const minWidth = 360;
      const maxWidth = 420;

      let width = Math.max(rect.width, minWidth);
      width = Math.min(width, maxWidth);

      let left = rect.left;
      if (left + width > window.innerWidth - viewportPad) {
        left = window.innerWidth - viewportPad - width;
      }
      if (left < viewportPad) {
        left = viewportPad;
        width = Math.min(width, window.innerWidth - viewportPad * 2);
      }

      let top = rect.bottom + gap;
      const panel = this.$refs.panel;
      const panelHeight = panel?.offsetHeight || 300;
      const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPad;
      const spaceAbove = rect.top - gap - viewportPad;

      if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
        top = Math.max(viewportPad, rect.top - gap - panelHeight);
      }

      this.panelStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        zIndex: 10050,
      };
    },
    attachPositionListeners() {
      window.addEventListener('scroll', this.updatePanelPosition, true);
      window.addEventListener('resize', this.updatePanelPosition);
    },
    detachPositionListeners() {
      window.removeEventListener('scroll', this.updatePanelPosition, true);
      window.removeEventListener('resize', this.updatePanelPosition);
    },
    onFocus() {
      if (this.selectedLabel) {
        this.query = '';
        this.$emit('clear');
      }
      this.isOpen = true;
      this.$nextTick(() => {
        if (this.query.trim().length >= this.minChars) {
          this.updatePanelPosition();
        }
      });
    },
    onInput(e) {
      this.query = e.target.value;
      this.isOpen = true;
      this.flatIndex = -1;
      this.$emit('query-change', this.query);
      this.$nextTick(() => {
        if (this.query.trim().length >= this.minChars) {
          this.updatePanelPosition();
        }
      });
    },
    onKeydown(e) {
      if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        this.isOpen = true;
        if (this.hasResults && e.key === 'ArrowDown') {
          e.preventDefault();
          this.flatIndex = 0;
          this.scrollActiveOptionIntoView();
        }
        return;
      }
      if (e.key === 'Escape') {
        this.isOpen = false;
        this.flatIndex = -1;
        this.$refs.input?.blur();
        return;
      }
      if (!this.hasResults) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = this.flatIndex < 0 ? 0 : this.flatIndex + 1;
        this.flatIndex = Math.min(next, this.flatResults.length - 1);
        this.scrollActiveOptionIntoView();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = this.flatIndex < 0 ? 0 : this.flatIndex - 1;
        this.flatIndex = Math.max(next, 0);
        this.scrollActiveOptionIntoView();
      } else if (e.key === 'Enter' && this.flatIndex >= 0) {
        e.preventDefault();
        this.selectItem(this.flatResults[this.flatIndex]);
      }
    },
    selectItem(item) {
      if (!item) return;
      this.query = '';
      this.isOpen = false;
      this.flatIndex = -1;
      this.$emit('query-change', '');
      this.$emit('select', {
        type: item.type,
        id: item.id,
        name: item.name
      });
    },
    clear() {
      this.query = '';
      this.isOpen = false;
      this.flatIndex = -1;
      this.$emit('query-change', '');
      this.$emit('clear');
      this.$nextTick(() => this.$refs.input?.focus());
    }
  },
  mounted() {
    if (!this.selectedLabel && this.externalQuery) {
      this.query = String(this.externalQuery);
    }
    document.addEventListener('mousedown', this.onDocumentMouseDown);
  },
  beforeUnmount() {
    this.isOpen = false;
    this.flatIndex = -1;
    this.panelStyle = {};
    this.detachPositionListeners();
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }
};
</script>

<style src="./CenterGlobalSearch.css"></style>
